import { memo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Search, Database, BarChart2, Check, X, Cpu } from 'lucide-react';

const STEP_ICONS = {
  parse: Sparkles,
  token: Cpu,
  search: Search,
};

const STEP_COLORS = {
  parse: '#8B5CF6',
  token: '#06B6D4',
  search: '#4FA490',
};

function stepIcon(stepId) {
  if (stepId.startsWith('fetch_')) return BarChart2;
  return STEP_ICONS[stepId] || Database;
}

function stepColor(stepId, index) {
  if (stepId.startsWith('fetch_')) {
    const fetchColors = ['#3B82F6', '#F59E0B', '#EC4899', '#10B981', '#EF4444', '#6366F1'];
    const i = parseInt(stepId.replace('fetch_', ''), 10);
    return fetchColors[i % fetchColors.length];
  }
  return STEP_COLORS[stepId] || '#64748B';
}

function StepRow({ step, index }) {
  const Icon = stepIcon(step.id);
  const color = stepColor(step.id, index);
  const isRunning = step.status === 'running';
  const isComplete = step.status === 'complete';
  const isError = step.status === 'error';
  const isPending = step.status === 'pending';

  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: isPending ? 0.4 : 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.06 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          py: 0.75,
        }}
      >
        {/* Icon circle */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: isRunning ? color : isComplete ? '#10B981' : isError ? '#EF4444' : '#E2E8F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.25,
            animation: isRunning ? 'agentPulse 1.4s ease-in-out infinite' : 'none',
            '@keyframes agentPulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%': { opacity: 0.65, transform: 'scale(0.92)' },
            },
            transition: 'background-color 0.3s ease',
          }}
        >
          {isComplete ? (
            <Check size={13} color='#fff' strokeWidth={3} />
          ) : isError ? (
            <X size={13} color='#fff' strokeWidth={3} />
          ) : (
            <Icon size={13} color={isPending ? '#94A3B8' : '#fff'} />
          )}
        </Box>

        {/* Text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Lato", sans-serif',
              fontSize: '13px',
              fontWeight: 600,
              color: isPending ? '#94A3B8' : isError ? '#EF4444' : '#1E293B',
              lineHeight: 1.4,
            }}
          >
            {step.label}
          </Typography>
          <AnimatePresence>
            {step.sublabel && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Lato", sans-serif',
                    fontSize: '11.5px',
                    color: isError ? '#EF4444' : '#64748B',
                    mt: 0.25,
                  }}
                >
                  {step.sublabel}
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* Running dots */}
        {isRunning && (
          <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center', mt: 0.5, flexShrink: 0 }}>
            {[0, 1, 2].map(i => (
              <Box
                key={i}
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: color,
                  animation: 'dotBounce 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  '@keyframes dotBounce': {
                    '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                    '40%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </motion.div>
  );
}

const QueryPlanBlock = memo(function QueryPlanBlock({
  query,
  parsedQuery,
  steps,
  status,
  elapsedSeconds,
}) {
  const runningCount = steps.filter(s => s.status === 'running').length;
  const completedCount = steps.filter(s => s.status === 'complete').length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: '14px',
          border: '1px solid #E2E8F0',
          borderLeft: '4px solid #8B5CF6',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            borderBottom: '1px solid #F1F5F9',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Sparkles size={16} color='#8B5CF6' />
            <Typography
              sx={{
                fontFamily: '"Lato", sans-serif',
                fontSize: '13px',
                fontWeight: 700,
                color: '#1E293B',
                flex: 1,
              }}
            >
              {query}
            </Typography>
            {status === 'running' && (
              <Typography
                sx={{
                  fontFamily: '"Lato", sans-serif',
                  fontSize: '11px',
                  color: '#94A3B8',
                  flexShrink: 0,
                }}
              >
                {runningCount > 0
                  ? `${runningCount} agent${runningCount > 1 ? 's' : ''} working · ${elapsedSeconds}s`
                  : `${elapsedSeconds}s`}
              </Typography>
            )}
            {status === 'complete' && (
              <Typography
                sx={{
                  fontFamily: '"Lato", sans-serif',
                  fontSize: '11px',
                  color: '#10B981',
                  flexShrink: 0,
                }}
              >
                {completedCount} completed · {elapsedSeconds}s
              </Typography>
            )}
          </Box>

          {/* Concept tags */}
          {parsedQuery?.tags?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {parsedQuery.tags.map(tag => (
                <Box
                  key={tag.label}
                  sx={{
                    px: 1,
                    py: 0.3,
                    borderRadius: '6px',
                    backgroundColor: `${tag.color}18`,
                    border: `1px solid ${tag.color}40`,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Lato", sans-serif',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: tag.color,
                      textTransform: 'capitalize',
                    }}
                  >
                    {tag.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Steps */}
        {steps.length > 0 && (
          <Box sx={{ px: 2.5, py: 1.5 }}>
            {steps
              .filter(step => {
                // Hide failed fetch steps when at least one fetch step succeeded
                if (step.id.startsWith('fetch_') && step.status === 'error') {
                  const anyFetchSucceeded = steps.some(
                    s => s.id.startsWith('fetch_') && s.status === 'complete',
                  );
                  return !anyFetchSucceeded;
                }
                return true;
              })
              .map((step, idx) => (
                <StepRow key={step.id} step={step} index={idx} />
              ))}
          </Box>
        )}
      </Paper>
    </motion.div>
  );
});

export default QueryPlanBlock;
